const COMFORTAA_BOLD_13 = '700 13px "Comfortaa Bold", Comfortaa, sans-serif';

const MIN_DATA_COLUMN_WIDTH = 52;
const MIN_NAME_COLUMN_WIDTH = 72;
const ACTION_COLUMN_WIDTH = 76;
/** Total horizontal padding inside the cell (left + right). */
const CELL_HORIZONTAL_PADDING = 20;
/** Icon + gap before name text in the first column. */
const NAME_COLUMN_LEADING = 22;
const CHEVRON_CHILD_INDENT = 20;

const textWidthCache = new Map<string, number>();

function measureTextWidth(text: string, font: string): number {
  const key = `${font}::${text}`;
  const cached = textWidthCache.get(key);
  if (cached !== undefined) return cached;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return MIN_DATA_COLUMN_WIDTH;

  context.font = font;
  const { width } = context.measureText(text || '');
  textWidthCache.set(key, width);
  return width;
}

function numberWithCommas(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function formatDataCellDisplayText(cell: any): string {
  const rawText = cell?.text;
  if (rawText == null || rawText === '') return '';

  if (!cell?.format) return String(rawText);

  const cellText = Number(rawText);
  if (Number.isNaN(cellText)) return String(rawText);

  const format = String(cell.format).toLowerCase();
  if (format === 'number') return numberWithCommas(cellText.toFixed(2));
  if (format === 'integer') return numberWithCommas(cellText.toFixed(0));
  if (format === 'percentage') return `${cellText.toFixed(1)}%`;

  if (format === 'currency') {
    const currency = cell.currencyType ?? '$';
    const formatted = numberWithCommas(Math.abs(cellText).toFixed(2));
    return cellText < 0 ? `(${currency}${formatted})` : `${currency}${formatted}`;
  }

  return String(rawText);
}

function getCellDisplayText(cell: any): string {
  if (!cell) return '';
  if (cell.type === 'header' || cell.colType === 'header') return String(cell.text ?? '');
  if (cell.property === 'name' || cell.className?.includes('name')) return String(cell.text ?? '');
  return formatDataCellDisplayText(cell);
}

function columnWidthFromTexts(
  texts: string[],
  font: string,
  minWidth: number,
  extraLeading = 0,
): number {
  const measured = texts.filter((text) => text !== '').map((text) => measureTextWidth(text, font));

  const contentWidth = measured.length > 0 ? Math.max(...measured) : 0;
  return Math.max(minWidth, Math.ceil(contentWidth + extraLeading + CELL_HORIZONTAL_PADDING));
}

function isDataRow(row: { rowId?: string }): boolean {
  return row.rowId !== 'header' && row.rowId !== 'footer';
}

function getContentBasedDimensionalColumns(indicatorHeaders: any[], rows: any[]) {
  const dataRows = rows.filter((row) => isDataRow(row));
  const headerRow = rows.find((row) => row.rowId === 'header');

  const nameTexts = ['Name'];
  let maxNameLeading = NAME_COLUMN_LEADING;

  dataRows.forEach((row) => {
    const nameCell = row.cells?.[0];
    if (!nameCell) return;

    nameTexts.push(getCellDisplayText(nameCell));

    if (String(nameCell.className ?? '').includes('chevron-child')) {
      maxNameLeading = Math.max(maxNameLeading, NAME_COLUMN_LEADING + CHEVRON_CHILD_INDENT);
    }
  });

  const nameWidth = columnWidthFromTexts(
    nameTexts,
    COMFORTAA_BOLD_13,
    MIN_NAME_COLUMN_WIDTH,
    maxNameLeading,
  );

  const periodColumns = (indicatorHeaders ?? []).map((header: any, index: number) => {
    const texts = [header?.name ?? ''];
    const headerCell = headerRow?.cells?.[index + 1];
    if (headerCell?.text) texts.push(String(headerCell.text));

    dataRows.forEach((row) => {
      const cell = row.cells?.[index + 1];
      texts.push(getCellDisplayText(cell));
    });

    return {
      columnId: `${header?.id}`,
      width: columnWidthFromTexts(texts, COMFORTAA_BOLD_13, MIN_DATA_COLUMN_WIDTH),
      resizable: true,
      reorderable: true,
    };
  });

  return [
    {
      columnId: 'name',
      width: nameWidth,
      resizable: true,
      reorderable: true,
    },
    ...periodColumns,
    { columnId: 'delete', width: ACTION_COLUMN_WIDTH },
  ];
}

export default getContentBasedDimensionalColumns;
